import {
    CollisionDescriptor,
    CollisionDetection,
    closestCenter,
    pointerWithin,
    rectIntersection
} from '@dnd-kit/core';
import useDndStore from '../../../sharedCode/stores/dndStore';

export const customCollisionDetectionAlgorithm = (args) => {
    // console.log('customCollision, ', args);
    let activeItem = useDndStore.getState().activeItem;
    // console.log('activeItem, ', activeItem);

    if (
        args.active.data?.current?.type === 'tab' ||
        activeItem?.dragType === 'tab'
    ) {
        return handleTabDragCollision(args);
    } else if (args.active.data?.current?.type === 'dragGroup') {
        return handleGroupDragCollision(args);
    }

    return null;
};

const handleTabDragCollision = (args) => {
    let { overContainerZu, dragIds } = useDndStore.getState();
    let filteredGroupContainers = args.droppableContainers.filter(
        (container) =>
            container.data.current.type === 'dragGroup' ||
            container.data.current.type === 'trashZone'
    );
    const pointerCollisions = pointerWithin({
        ...args,
        droppableContainers: filteredGroupContainers
    });
    if (pointerCollisions.length === 0) {
        useDndStore.setState({ overContainerZu: null, insertPosition: {} });
        return null;
    }
    if (
        pointerCollisions[0]?.data?.droppableContainer?.data?.current?.isActive
    ) {
        if (!args.active.data?.current?.isActiveTab) {
            useDndStore.setState({ overContainerZu: null, insertPosition: {} });
            return null;
        }
    }
    let overContainerObject = pointerCollisions[0];
    if (pointerCollisions.length > 1) {
        pointerCollisions.forEach((container) => {
            if (container.data?.droppableContainer?.data?.current?.isActive) {
                overContainerObject = container;
            } else if (container.id === 'TrashDropZone') {
                overContainerObject = container;
            }
        });
    }

    let overContainerRaw = overContainerObject.id;
    if (overContainerZu !== overContainerRaw) {
        useDndStore.setState({
            overContainerZu: overContainerRaw
        });
    }
    if (overContainerRaw === 'ContainerPlaceholder') {
        useDndStore.setState({
            insertPosition: {
                [overContainerRaw]: 'placeholder'
            }
        });
        return;
    } else if (overContainerRaw === 'TrashDropZone') {
        useDndStore.setState({
            insertPosition: {
                [overContainerRaw]: 'TrashDropZone'
            }
        });
        return;
    }

    let filteredTabContainers = args.droppableContainers.filter(
        (container) =>
            container.data.current.type === 'tab' &&
            container.data.current.groupId === overContainerRaw &&
            !dragIds.includes(container?.id)
    );

    if (filteredTabContainers.length === 0) {
        useDndStore.getState().setInsertPosition({
            [overContainerRaw]: 'emptyContainer'
        });

        return;
    }
    let { closestContainer, pointerPositionRelativeToContainer } =
        getClosestTabContainer(
            filteredTabContainers,
            args.pointerCoordinates.y
        );
    if (closestContainer.id === args.active.id) {
        return;
    }

    let insertPosition = useDndStore.getState().insertPosition;
    if (!(closestContainer.id in insertPosition)) {
        useDndStore.getState().setInsertPosition({
            [closestContainer.id]: pointerPositionRelativeToContainer
        });
    } else if (
        pointerPositionRelativeToContainer !==
        insertPosition[closestContainer.id]
    ) {
        useDndStore.getState().setInsertPosition({
            [closestContainer.id]: pointerPositionRelativeToContainer
        });
    }
};

const Position = {
    OVER: 'over',
    UNDER: 'under',
    LEFT: 'left',
    RIGHT: 'right',
    CENTER: 'center'
};
const getClosestTabContainer = (filteredTabContainers, pointerCoordinatesY) => {
    let closestContainer;
    let closestDistance = Infinity;
    let pointerPositionRelativeToContainer = Position.CENTER;
    filteredTabContainers.forEach((container) => {
        const distanceFromTop = Math.abs(
            container.rect.current.top - pointerCoordinatesY
        );
        const distanceFromBottom = Math.abs(
            container.rect.current.bottom - pointerCoordinatesY
        );
        const containerHeight =
            container.rect.current.bottom - container.rect.current.top;
        const topThreshold = container.rect.current.top + containerHeight * 0.3;
        const bottomThreshold =
            container.rect.current.bottom - containerHeight * 0.3;

        let distanceToContainer = Math.min(distanceFromTop, distanceFromBottom);

        if (distanceToContainer < closestDistance) {
            closestDistance = distanceToContainer;
            closestContainer = container;

            if (pointerCoordinatesY < topThreshold) {
                pointerPositionRelativeToContainer = Position.OVER;
            } else if (pointerCoordinatesY > bottomThreshold) {
                pointerPositionRelativeToContainer = Position.UNDER;
            } else {
                pointerPositionRelativeToContainer = Position.CENTER;
            }
        }
    });

    if (
        closestContainer?.data?.current?.isActiveGroup &&
        pointerPositionRelativeToContainer === 'over'
    ) {
        let topThreshold = closestContainer.rect.current.top + 10;
        if (pointerCoordinatesY > topThreshold) {
            pointerPositionRelativeToContainer = Position.CENTER;
        }
    }
    return { closestContainer, pointerPositionRelativeToContainer };
};

const getClosestGroupContainerAndPosition = (
    filteredTabContainers,
    pointerCoordinates
) => {
    let closestContainer;
    let closestDistance = Infinity;
    let pointerPositionRelativeToContainer = Position.CENTER;
    filteredTabContainers.forEach((container) => {
        const rect = container.rect.current;
        const centerX = (rect.left + rect.right) / 2;
        const centerY = (rect.top + rect.bottom) / 2;

        // Correctly define centralWidth and centralHeight based on container dimensions
        const centralWidth = rect.right - rect.left;
        const centralHeight = rect.bottom - rect.top;

        const distanceToCenter = Math.sqrt(
            Math.pow(centerX - pointerCoordinates.x, 2) +
                Math.pow(centerY - pointerCoordinates.y, 2)
        );

        if (distanceToCenter < closestDistance) {
            closestDistance = distanceToCenter;
            closestContainer = container;

            const centralLeft = rect.left + centralWidth * 0.2; // 20% from left for 60% width central area
            const centralTop = rect.top + centralHeight * 0.1; // 10% from top for 80% height central area
            const isPointerInCentralArea =
                pointerCoordinates.x >= centralLeft &&
                pointerCoordinates.x <= centralLeft + centralWidth * 0.6 &&
                pointerCoordinates.y >= centralTop &&
                pointerCoordinates.y <= centralTop + centralHeight * 0.8;

            if (isPointerInCentralArea) {
                pointerPositionRelativeToContainer = Position.CENTER;
            } else {
                // Outside central area but still determining closest edge
                if (pointerCoordinates.x < rect.left) {
                    pointerPositionRelativeToContainer = Position.LEFT;
                } else if (pointerCoordinates.x > rect.right) {
                    pointerPositionRelativeToContainer = Position.RIGHT;
                } else if (pointerCoordinates.y < rect.top) {
                    pointerPositionRelativeToContainer = Position.OVER;
                } else if (pointerCoordinates.y > rect.bottom) {
                    pointerPositionRelativeToContainer = Position.UNDER;
                } else {
                    // Determine the closest edge when inside the bounds but not in the central area
                    const distanceToLeft = pointerCoordinates.x - rect.left;
                    const distanceToRight = rect.right - pointerCoordinates.x;
                    const distanceToTop = pointerCoordinates.y - rect.top;
                    const distanceToBottom = rect.bottom - pointerCoordinates.y;
                    const minDistance = Math.min(
                        distanceToLeft,
                        distanceToRight,
                        distanceToTop,
                        distanceToBottom
                    );

                    if (minDistance === distanceToLeft) {
                        pointerPositionRelativeToContainer = Position.LEFT;
                    } else if (minDistance === distanceToRight) {
                        pointerPositionRelativeToContainer = Position.RIGHT;
                    } else if (minDistance === distanceToTop) {
                        pointerPositionRelativeToContainer = Position.OVER;
                    } else if (minDistance === distanceToBottom) {
                        pointerPositionRelativeToContainer = Position.UNDER;
                    }
                }
            }
        }
    });

    return { closestContainer, pointerPositionRelativeToContainer };
};

const getClosestHorizontalContainerAndPosition = (
    filteredTabContainers,
    pointerCoordinates
) => {
    let closestContainer;
    let closestDistance = Infinity;
    let pointerPositionRelativeToContainer = Position.CENTER;
    filteredTabContainers.forEach((container) => {
        if (
            container.id === 'ContainerPlaceholder' ||
            container.data.current.isActive
        ) {
            return;
        }
        const rect = container.rect.current;

        // Determine the edge distances
        const distanceToLeftEdge = Math.abs(rect.left - pointerCoordinates.x);
        const distanceToRightEdge = Math.abs(rect.right - pointerCoordinates.x);

        // Determine the closest edge distance
        const closestEdgeDistance = Math.min(
            distanceToLeftEdge,
            distanceToRightEdge
        );

        // Calculate the center region
        const containerWidth = rect.right - rect.left;
        const centralRegionStartX = rect.left + containerWidth * 0.3; // 30% to start the center region
        const centralRegionEndX = rect.right - containerWidth * 0.3; // Remaining 30% from the right

        // Determine if the pointer is closer to the center or an edge
        const isPointerInCenterRegion =
            pointerCoordinates.x >= centralRegionStartX &&
            pointerCoordinates.x <= centralRegionEndX;

        // Choose the smaller of the center distance and the closest edge distance for comparison
        let comparisonDistance = closestEdgeDistance;
        if (isPointerInCenterRegion) {
            const centerX = (rect.left + rect.right) / 2;
            const distanceToCenterX = Math.abs(centerX - pointerCoordinates.x);
            comparisonDistance = Math.min(
                comparisonDistance,
                distanceToCenterX
            );
        }

        // Update closest container and position if a new closest distance is found
        if (comparisonDistance < closestDistance) {
            closestDistance = comparisonDistance;
            closestContainer = container;

            if (isPointerInCenterRegion) {
                pointerPositionRelativeToContainer = Position.CENTER;
            } else if (pointerCoordinates.x < centralRegionStartX) {
                pointerPositionRelativeToContainer = Position.LEFT;
            } else {
                pointerPositionRelativeToContainer = Position.RIGHT;
            }
        }
    });

    return { closestContainer, pointerPositionRelativeToContainer };
};
const getClosestHorizontalContainer = (
    filteredTabContainers,
    pointerCoordinates
) => {
    let closestContainer;
    let closestDistance = Infinity;
    filteredTabContainers.forEach((container) => {
        if (
            container.id === 'ContainerPlaceholder' ||
            container.data.current.isActive
        ) {
            return;
        }
        const rect = container.rect.current;

        const distanceToLeftEdge = Math.abs(rect.left - pointerCoordinates.x);
        const distanceToRightEdge = Math.abs(rect.right - pointerCoordinates.x);

        const closestEdgeDistance = Math.min(
            distanceToLeftEdge,
            distanceToRightEdge
        );

        if (closestEdgeDistance < closestDistance) {
            closestDistance = closestEdgeDistance;
            closestContainer = container;
        }
    });

    return closestContainer;
};

const handleGroupDragCollision = (args) => {
    // return closestCenter(args);

    let filteredGroupContainers = args.droppableContainers.filter(
        (container) => container.data.current.type === 'dragGroup'
    );

    let closestContainer = getClosestHorizontalContainer(
        filteredGroupContainers,
        args.pointerCoordinates
    );

    return [closestContainer];
};
